import { pages, subPackages, tabBar } from '@/pages.json'
import { isMpWeixin, isH5, isApp } from './platform'
import { useUserStore } from '@/store'
import { useI18n } from 'vue-i18n'
import i18n from '@/locales'

export const pathLogin = isH5 || isApp ? '/p-user/pages/login/login' : '/p-user/pages/login/index'
export const getLastPage = () => {
  // getCurrentPages() 至少有1个元素，所以不再额外判断
  // const lastPage = getCurrentPages().at(-1)
  // 上面那个在低版本安卓中打包会报错，所以改用下面这个【虽然我加了 src/interceptions/prototype.ts，但依然报错】
  const pages = getCurrentPages()
  return pages[pages.length - 1]
}

/** 判断当前页面是否是 tabbar 页  */
export const getIsTabbar = () => {
  if (!tabBar) {
    return false
  }
  if (!tabBar.list.length) {
    // 通常有 tabBar 的话，list 不能有空，且至少有2个元素，这里其实不用处理
    return false
  }
  const lastPage = getLastPage()
  const currPath = lastPage.route
  return !!tabBar.list.find((e) => e.pagePath === currPath)
}

/**
 * 获取当前页面路由的 path 路径和 redirectPath 路径
 * path 如 '/p-user/pages/login/index'
 * redirectPath 如 '/pages/demo/base/route-interceptor'
 */
export const currRoute = () => {
  const lastPage = getLastPage()
  const currRoute = (lastPage as any).$page
  // console.log('lastPage.$page:', currRoute)
  // console.log('lastPage.$page.fullpath:', currRoute.fullPath)
  // console.log('lastPage.$page.options:', currRoute.options)
  // console.log('lastPage.options:', (lastPage as any).options)
  // 经过多端测试，只有 fullPath 靠谱，其他都不靠谱
  const { fullPath } = currRoute as { fullPath: string }
  // console.log(fullPath)
  // eg: /p-user/pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor (小程序)
  // eg: /p-user/pages/login/index?redirect=%2Fpages%2Froute-interceptor%2Findex%3Fname%3Dfeige%26age%3D30(h5)
  return getUrlObj(fullPath)
}

const ensureDecodeURIComponent = (url: string) => {
  // 使用 i18n 实例而不是 useI18n hook
  const { t } = i18n.global

  if (url.startsWith(t('%'))) {
    return ensureDecodeURIComponent(decodeURIComponent(url))
  }
  return url
}
/**
 * 解析 url 得到 path 和 query
 * 比如输入url: /p-user/pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
 * 输出: {path: /p-user/pages/login/index, query: {redirect: /pages/demo/base/route-interceptor}}
 */
export const getUrlObj = (url: string) => {
  const [path, queryStr] = url.split('?')
  // console.log(path, queryStr)

  if (!queryStr) {
    return {
      path,
      query: {},
    }
  }
  const query: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    // console.log(key, value)
    query[key] = ensureDecodeURIComponent(value) // 这里需要统一 decodeURIComponent 一下，可以兼容h5和微信y
  })
  return { path, query }
}
/**
 * 得到所有的需要登录的 pages，包括主包和分包的
 * 这里设计得通用一点，可以传递 key 作为判断依据，默认是 needLogin, 与 route-block 配对使用
 * 如果没有传 key，则表示所有的 pages，如果传递了 key, 则表示通过 key 过滤
 */
export const getAllPages = (key = 'needLogin') => {
  // 这里处理主包
  const mainPages = [
    ...pages
      .filter((page) => !key || page[key])
      .map((page) => ({
        ...page,
        path: `/${page.path}`,
      })),
  ]
  // 这里处理分包
  const subPages: any[] = []
  subPackages.forEach((subPageObj) => {
    // console.log(subPageObj)
    const { root } = subPageObj

    subPageObj.pages
      .filter((page) => !key || page[key])
      .forEach((page: { path: string } & Record<string, any>) => {
        subPages.push({
          ...page,
          path: `/${root}/${page.path}`,
        })
      })
  })
  const result = [...mainPages, ...subPages]
  // console.log(`getAllPages by ${key} result: `, result)
  return result
}

/**
 * 得到所有的需要登录的 pages，包括主包和分包的
 * 只得到 path 数组
 */
export const getNeedLoginPages = (): string[] => getAllPages('needLogin').map((page) => page.path)

/**
 * 得到所有的需要登录的 pages，包括主包和分包的
 * 只得到 path 数组
 */
export const needLoginPages: string[] = getAllPages('needLogin').map((page) => page.path)

/**
 * 根据微信小程序当前环境，判断应该获取的 baseUrl
 */
export const getEnvBaseUrl = () => {
  // 请求基准地址
  let baseUrl = import.meta.env.VITE_SERVER_BASEURL

  // 微信小程序端环境区分
  if (isMpWeixin) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    switch (envVersion) {
      case 'develop':
        baseUrl = import.meta.env.VITE_SERVER_BASEURL__WEIXIN_DEVELOP || baseUrl
        break
      case 'trial':
        baseUrl = import.meta.env.VITE_SERVER_BASEURL__WEIXIN_TRIAL || baseUrl
        break
      case 'release':
        baseUrl = import.meta.env.VITE_SERVER_BASEURL__WEIXIN_RELEASE || baseUrl
        break
    }
  }

  return baseUrl
}

/**
 * 根据微信小程序当前环境，判断应该获取的 UPLOAD_BASEURL
 */
export const getEnvBaseUploadUrl = () => {
  // 请求基准地址
  let baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL

  // 微信小程序端环境区分
  if (isMpWeixin) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    switch (envVersion) {
      case 'develop':
        baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL__WEIXIN_DEVELOP || baseUploadUrl
        break
      case 'trial':
        baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL__WEIXIN_TRIAL || baseUploadUrl
        break
      case 'release':
        baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL__WEIXIN_RELEASE || baseUploadUrl
        break
    }
  }

  return baseUploadUrl
}

export const copyText = (text: string) => {
  // 使用 i18n 实例而不是 useI18n hook
  const { t } = i18n.global /* eslint-disable */

  uni
    .setClipboardData({
      data: text,
    })
    .then(() => {
      uni.showToast({
        title: t('fuZhiChengGong'),
        icon: 'none',
      })
    })
}

export const px2 = (px: number) => {
  const screenWidth = uni.getSystemInfoSync().screenWidth
  return (px / 375) * screenWidth
}
const screenWidth = uni.getSystemInfoSync().screenWidth
export const navBarHeight = ((44 * 750) / 375) * (screenWidth / 375)
export const navBarTop = uni.getSystemInfoSync()?.safeAreaInsets?.top
export const toPage = (url: string, options?: { isReLaunch?: boolean }) => {
  // 使用 i18n 实例而不是 useI18n hook
  const { t } = i18n.global /* eslint-disable */

  const userStore = useUserStore()
  if (userStore.isLogin) {
    if (options?.isReLaunch) {
      uni.reLaunch({
        url,
      })
    } else {
      uni.navigateTo({
        url,
      })
    }
  } else {
    toast(t('ninShangWeiDengLuQingXianDengLuHouZaiChaKan'))
    setTimeout(() => {
      uni.redirectTo({
        url: pathLogin,
      })
    }, 1500)
  }
}
export const toast = (title: string) => {
  uni.showToast({
    title,
    icon: 'none',
  })
}

/**
 * 图片路径转换成base64，（支持临时路径，网络路径）
 * @param filePath
 * @returns
 */
export const fileToBase64 = (filePath) => {
  return new Promise((resolve, reject) => {
    // #ifdef H5
    const reader = new FileReader()
    // 通过 uni.chooseImage 获取的是 Blob/File 对象（H5）
    // 如果是网络路径，需要先 fetch 获取 Blob
    fetch(filePath)
      .then((res) => res.blob())
      .then((blob) => {
        reader.readAsDataURL(blob)
        reader.onload = () => {
          const base64 = reader.result
          // console.log(base64)
          resolve(base64)
        }
      })
      .catch((err) => {
        reject(err)
      })
    // #endif

    // #ifdef MP-WEIXIN
    // 小程序使用 wx.getFileSystemManager()
    const fs = wx.getFileSystemManager()
    fs.readFile({
      filePath,
      encoding: 'base64', // 关键：指定返回 Base64
      success: (res) => {
        const base64 = `data:image/jpeg;base64,${res.data}`
        // console.log(base64)
        resolve(base64)
      },
      fail: (err) => {
        console.error('读取失败:', err)
        reject(err)
      },
    })
    // #endif

    // #ifdef APP-PLUS
    plus.io.resolveLocalFileSystemURL(
      filePath,
      (entry: any) => {
        entry.file((file) => {
          const reader = new plus.io.FileReader()
          reader.onloadend = (e: any) => {
            const base64 = e.target.result as any
            // console.log(base64)
            resolve(base64)
          }
          reader.readAsDataURL(file)
        })
      },
      (err) => {
        console.error('读取失败:', err)
        reject(err)
      },
    )
    // #endif
  })
}

/**
 * 获取网络图片并转换为base64
 * @param imageUrl
 * @returns
 */
export const getImageBase64 = (imageUrlHttp) => {
  // 使用 i18n 实例而不是 useI18n hook
  const { t } = i18n.global /* eslint-disable */

  return new Promise((resolve, reject) => {
    uni.request({
      url: imageUrlHttp,
      method: 'GET',
      responseType: 'arraybuffer', // 重要！指定响应数据类型为二进制
      success: (res: any) => {
        console.log(res)
        const ext = getImageExtensionFromUrl(imageUrlHttp)
        if (['png', 'jpg', 'jpeg'].includes(ext)) {
          // 将ArrayBuffer转换为Base64
          const base64 = uni.arrayBufferToBase64(res.data)
          const base64Url = `data:image/${ext};base64,${base64}`
          resolve(base64Url)
        } else {
          reject(new Error(t('invalidImageUrl')))
        }
      },
      fail: (err) => {
        reject(err)
      },
    })
  })
}

/**
 * 获取网络图片并转换为临时路径
 * @param imageUrlHttp
 * @returns
 */
export const getTempPath = (imageUrlHttp) => {
  return new Promise((resolve, reject) => {
    uni.downloadFile({
      url: imageUrlHttp,
      success: (res) => {
        if (res.statusCode === 200) {
          resolve(res.tempFilePath)
        }
      },
    })
  })
}

/**
 * 从 URL 路径中提取 图片后缀
 * @param url
 * @returns
 */
export const getImageExtensionFromUrl = (url: any) => {
  const path = url.split('?')[0]
  const extension = path.split('.').pop().toLowerCase()
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']
  return imageExtensions.includes(extension) ? extension : null
}

const env = import.meta.env.VITE_ENV
// export const getStoreKey = (key) => (!env || env === 'prod' ? key : `${key}__${env}`)
export const getStoreKey = (key) => key

export const getStorage = (key) => {
  try {
    return uni.getStorageSync(getStoreKey(key))
  } catch (error) {
    console.warn('getStorage failed:', error)
    return null
  }
}

export const setStorage = (key, value) => {
  try {
    uni.setStorageSync(getStoreKey(key), value)
  } catch (error) {
    console.warn('setStorage failed:', error)
    // 如果同步存储失败，尝试异步存储
    try {
      uni.setStorage({
        key: getStoreKey(key),
        data: value,
        fail: (asyncError) => {
          console.error('Async storage also failed:', asyncError)
        },
      })
    } catch (asyncError) {
      console.error('Async storage setup failed:', asyncError)
    }
  }
}

export const removeStorage = (key?: string) => {
  if (!key) {
    try {
      uni.getStorageInfo({
        success: (res) => {
          res.keys.forEach((key) => {
            try {
              const keyname = key.split('__')[0]
              uni.removeStorageSync(getStoreKey(keyname))
            } catch (error) {
              console.warn('removeStorage failed for key:', key, error)
            }
          })
          // console.log('所有缓存 key:', res.keys) // 返回所有 key 的数组
          // console.log('当前缓存大小:', res.currentSize) // 当前占用空间（KB）
          // console.log('缓存限制大小:', res.limitSize) // 最大限制（KB）
        },
        fail: (error) => {
          console.warn('getStorageInfo failed:', error)
        },
      })
    } catch (error) {
      console.warn('removeStorage (all keys) failed:', error)
    }
  } else {
    try {
      uni.removeStorageSync(getStoreKey(key))
    } catch (error) {
      console.warn('removeStorage failed:', error)
    }
  }
}

/**
 * 保存分销链接码
 * @param code
 */
export const saveInviteLinkCode = async (code) => {
  let store = null
  try {
    const oldStore = JSON.parse(getStorage('invite_link_code'))
    if (oldStore && oldStore.due_time && oldStore.value) {
      store = oldStore
    }
  } catch (e) {}
  const limit = 1000 * 60 * 60 * 24 * 5
  const now = Date.now()
  if (!store || (store.value !== code && store.due_time + limit < now)) {
    store = {
      value: code,
      due_time: now + limit,
    }
  }
  setStorage('invite_link_code', JSON.stringify(store))
  await new Promise((resolve) => setTimeout(resolve, 100))
}

/**
 * 获取分销链接码
 * @returns
 */
export const getInviteLinkCode = () => {
  let store = null
  try {
    const oldStore = JSON.parse(getStorage('invite_link_code'))
    if (oldStore && oldStore.value) {
      store = oldStore
    }
  } catch (e) {}
  return store ? store.value : ''
}

/**
 * 跳转登录页面
 * @param opt
 */
export const goLogin = (opt?: {
  redirect?: string | { url: string; params?: Record<string, any> }
  isReLaunch?: boolean
  isRedirectTo?: boolean
  params?: Record<string, any>
}) => {
  // 使用 i18n 实例而不是 useI18n hook
  const { t } = i18n.global /* eslint-disable */

  // 清除用户
  const userStore = useUserStore()
  userStore.clearUserInfo()

  if (opt?.redirect) {
    if (typeof opt.redirect === 'string') {
      getApp().globalData.loginSuccessRedirect = opt.redirect
    } else if (opt.redirect?.url) {
      let redirectPath = opt.redirect.url
      if (typeof opt.redirect?.params === 'object') {
        let paramString = ''
        for (const k in opt.redirect.params)
          paramString += `${k}=${encodeURIComponent(opt.redirect.params[k])}&`
        paramString = paramString.slice(0, -1)
        redirectPath = `${redirectPath}?${paramString}`
      }
      getApp().globalData.loginSuccessRedirect = redirectPath
    }
  } else {
    const globalData = getApp().globalData
    delete globalData.loginSuccessRedirect
  }

  let queryString = ''
  if (opt?.params) {
    for (const k in opt.params) {
      queryString += `${k}=${opt.params[k]}&`
    }
  }
  if (queryString) {
    queryString = `?${queryString.slice(0, -1)}`
  }

  if (opt?.isReLaunch) {
    uni.reLaunch({ url: `${pathLogin}${queryString}` })
  } else if (opt?.isRedirectTo) {
    uni.redirectTo({ url: `${pathLogin}${queryString}` })
  } else {
    uni.navigateTo({ url: `${pathLogin}${queryString}` })
  }
}

export const shareAppMessage = () => {
  // 使用 i18n 实例而不是 useI18n hook
  const { t } = i18n.global

  return {
    title: t('1688QuanGuoShouGeKuaJingXuanPinZhongXin'), // 分享标题
    path: '/pages/index/index', // 默认分享路径，可带参数
    imageUrl: '@img/share.jpg', // 分享图片
  }
}
export const debounce = (fn: any, delay: number = 300) => {
  let timer: number | null = null
  return (...args: any[]) => {
    timer && clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

export function getCookieDomain() {
  if (typeof window === 'undefined') return ''

  // 获取当前页面的主机名
  const hostname = window.location.hostname

  // 如果是 localhost 或 IP 地址，直接返回空字符串（表示当前域名）
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return ''
  }

  // 解析域名部分，获取二级域名
  const domainParts = hostname.split('.')

  // 如果是二级域名（如 example.com），返回完整的域名
  if (domainParts.length === 2) {
    return `.${hostname}`
  }

  // 如果是三级或更多级域名（如 sub.example.com），返回二级域名
  if (domainParts.length > 2) {
    // 如果包含dev,test,uat等环境标识，返回二级域名
    if (
      domainParts.includes('dev') ||
      domainParts.includes('test') ||
      domainParts.includes('uat')
    ) {
      const secondLevelDomain = domainParts.slice(1).join('.')
      return `.${secondLevelDomain}`
    }
    // 否则返回三级域名
    const secondLevelDomain = domainParts.slice(-2).join('.')
    return `.${secondLevelDomain}`
  }

  // 其他情况返回空字符串
  return ''
}
